import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList,
  Package,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Printer,
  EyeOff,
  Camera,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssPrintDocumentHeader from "../../components/oss/OssPrintDocumentHeader";
import EstraiSpesaDaScontrinoDialog from "../../components/prima-nota/EstraiSpesaDaScontrinoDialog";
import { printOssPage } from "../../lib/oss/print";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_AUSILIO = [
  { value: "pannoloni", label: "Pannoloni" },
  { value: "traverse", label: "Traverse" },
];

const TIPI_SPESA = [
  { value: "parrucchiere", label: "Parrucchiere" },
  { value: "podologo", label: "Podologo" },
  { value: "visita_specialistica", label: "Visita Specialistica" },
  { value: "acquisto_ausili", label: "Acquisto Ausili" },
  { value: "altro", label: "Altro" },
];

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function getMeseCorrente() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatMese(mese) {
  const [year, month] = mese.split("-");
  return `${MESI[parseInt(month) - 1]} ${year}`;
}

function navigateMese(mese, dir) {
  const [year, month] = mese.split("-").map(Number);
  const d = new Date(year, month - 1 + dir, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("it-IT");
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function isOssPrimaNotaRow(row) {
  return (row?.registratoDaTipo || "").toLowerCase() === "oss";
}

function BillingExclusionToggle({ id, checked, onCheckedChange }) {
  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      checked ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"
    }`}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          className="mt-0.5 h-5 w-5 rounded-md border-amber-300 data-[state=checked]:bg-amber-500 data-[state=checked]:text-white"
        />
        <Label htmlFor={id} className="flex-1 cursor-pointer">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <EyeOff className="h-4 w-4 text-amber-600" />
            Escludi dalla fatturazione
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-500">
            La voce resta in Prima Nota ma non verrà proposta nelle fatture.
          </span>
        </Label>
      </div>
    </div>
  );
}

const EMPTY_CONSUMO = {
  ospiteId: "",
  tipoAusilio: "",
  data: todayISO(),
  escludiDaFatturazione: false,
};
const EMPTY_SPESA = {
  ospiteId: "",
  tipo: "",
  data: todayISO(),
  escludiDaFatturazione: false,
};

export default function OssPrimaNota() {
  const { token } = useOssAuth();
  const [mese, setMese] = useState(getMeseCorrente);
  const [consumi, setConsumi] = useState([]);
  const [spese, setSpese] = useState([]);
  const [fascicoli, setFascicoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConsumoDialog, setShowConsumoDialog] = useState(false);
  const [consumoEditId, setConsumoEditId] = useState(null);
  const [consumoForm, setConsumoForm] = useState(EMPTY_CONSUMO);
  const [showSpesaDialog, setShowSpesaDialog] = useState(false);
  const [spesaEditId, setSpesaEditId] = useState(null);
  const [spesaForm, setSpesaForm] = useState(EMPTY_SPESA);
  const [showSpesaAiDialog, setShowSpesaAiDialog] = useState(false);

  const [consumoDialogKey, setConsumoDialogKey] = useState(0);
  const [spesaDialogKey, setSpesaDialogKey] = useState(0);
  const [primaNotaTab, setPrimaNotaTab] = useState("consumi");
  const consumoFieldDefaultsRef = useRef({ quantita: "1", note: "" });
  const spesaFieldDefaultsRef = useRef({ importo: "", descrizione: "", note: "" });
  const consumoQuantitaRef = useRef(null);
  const consumoNoteRef = useRef(null);
  const spesaImportoRef = useRef(null);
  const spesaDescrizioneRef = useRef(null);
  const spesaNoteRef = useRef(null);

  const ospiteMap = useMemo(() => {
    const m = {};
    fascicoli.forEach((f) => { m[f.id] = f; });
    return m;
  }, [fascicoli]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/fascicoli`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setFascicoli(data || []))
      .catch(() => setFascicoli([]));
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/oss/me/prima-nota/consumi`, {
          params: { mese },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/oss/me/prima-nota/spese`, {
          params: { mese },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setConsumi(cRes.data.consumi || []);
      setSpese(sRes.data.spese || []);
    } catch {
      toast.error("Errore nel caricamento dei dati");
      setConsumi([]);
      setSpese([]);
    } finally {
      setLoading(false);
    }
  }, [token, mese]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddConsumo = () => {
    setConsumoEditId(null);
    consumoFieldDefaultsRef.current = { quantita: "1", note: "" };
    setConsumoDialogKey((k) => k + 1);
    setConsumoForm({ ...EMPTY_CONSUMO, data: todayISO() });
    setShowConsumoDialog(true);
  };

  const openEditConsumo = (c) => {
    if (!isOssPrimaNotaRow(c) || !c.id) return;
    setConsumoEditId(c.id);
    consumoFieldDefaultsRef.current = {
      quantita: String(c.quantita ?? 1),
      note: c.note || "",
    };
    setConsumoDialogKey((k) => k + 1);
    setConsumoForm({
      ospiteId: c.ospiteId || "",
      tipoAusilio: c.tipoAusilio || "",
      data: c.data || todayISO(),
      escludiDaFatturazione: !!c.escludiDaFatturazione,
    });
    setShowConsumoDialog(true);
  };

  const handleSaveConsumo = async () => {
    if (!consumoForm.ospiteId) return toast.error("Seleziona un ospite");
    if (!consumoForm.tipoAusilio) return toast.error("Seleziona il tipo di ausilio");
    const qRaw = consumoQuantitaRef.current?.value ?? "1";
    const quantita = parseInt(String(qRaw), 10);
    if (!quantita || quantita < 1) {
      return toast.error("Inserisci una quantità valida (pezzi singoli)");
    }
    if (!consumoForm.data) return toast.error("Seleziona la data");
    const noteRaw = consumoNoteRef.current?.value?.trim() ?? "";
    const body = {
      ospiteId: consumoForm.ospiteId,
      tipoAusilio: consumoForm.tipoAusilio,
      quantita,
      data: consumoForm.data,
      note: noteRaw || null,
      escludiDaFatturazione: !!consumoForm.escludiDaFatturazione,
    };
    setSaving(true);
    try {
      if (consumoEditId) {
        await axios.put(
          `${API_URL}/api/oss/me/prima-nota/consumi/${consumoEditId}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Consumo aggiornato");
      } else {
        await axios.post(
          `${API_URL}/api/oss/me/prima-nota/consumi`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Consumo registrato");
      }
      setShowConsumoDialog(false);
      setConsumoEditId(null);
      consumoFieldDefaultsRef.current = { quantita: "1", note: "" };
      setConsumoForm({ ...EMPTY_CONSUMO, data: todayISO() });
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConsumo = async (c) => {
    if (!isOssPrimaNotaRow(c) || !c.id) return;
    if (!window.confirm("Eliminare questo consumo?")) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/oss/me/prima-nota/consumi/${c.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Consumo eliminato");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setSaving(false);
    }
  };

  const openAddSpesa = () => {
    setSpesaEditId(null);
    spesaFieldDefaultsRef.current = { importo: "", descrizione: "", note: "" };
    setSpesaDialogKey((k) => k + 1);
    setSpesaForm({ ...EMPTY_SPESA, data: todayISO() });
    setShowSpesaDialog(true);
  };

  const applySpesaFromAi = (fields) => {
    const importoStr =
      fields.importo != null && fields.importo !== ""
        ? String(fields.importo).replace(".", ",")
        : "";
    spesaFieldDefaultsRef.current = {
      importo: importoStr,
      descrizione: fields.descrizione || "",
      note: fields.note || "",
    };
    setSpesaDialogKey((k) => k + 1);
    setSpesaEditId(null);
    setSpesaForm((prev) => ({
      ...EMPTY_SPESA,
      ...prev,
      ospiteId: fields.ospiteId || prev.ospiteId || "",
      tipo: fields.tipo || prev.tipo || "",
      data: fields.data || prev.data || todayISO(),
      escludiDaFatturazione: !!prev.escludiDaFatturazione,
    }));
    setShowSpesaDialog(true);
    toast.success("Dati dallo scontrino applicati — verifica e salva");
  };

  const openEditSpesa = (s) => {
    if (!isOssPrimaNotaRow(s) || !s.id) return;
    setSpesaEditId(s.id);
    spesaFieldDefaultsRef.current = {
      importo: s.importo != null ? String(s.importo) : "",
      descrizione: s.descrizione || "",
      note: s.note || "",
    };
    setSpesaDialogKey((k) => k + 1);
    setSpesaForm({
      ospiteId: s.ospiteId || "",
      tipo: s.tipo || "",
      data: s.data || todayISO(),
      escludiDaFatturazione: !!s.escludiDaFatturazione,
    });
    setShowSpesaDialog(true);
  };

  const handleSaveSpesa = async () => {
    if (!spesaForm.ospiteId) return toast.error("Seleziona un ospite");
    if (!spesaForm.tipo) return toast.error("Seleziona il tipo di spesa");
    const importoRaw = spesaImportoRef.current?.value ?? "";
    const importoNum = parseFloat(String(importoRaw).replace(",", "."));
    if (!importoRaw.trim() || Number.isNaN(importoNum) || importoNum <= 0) {
      return toast.error("Inserisci un importo valido");
    }
    if (!spesaForm.data) return toast.error("Seleziona la data");
    const descrizioneRaw = spesaDescrizioneRef.current?.value?.trim() ?? "";
    const noteRaw = spesaNoteRef.current?.value?.trim() ?? "";
    const body = {
      ospiteId: spesaForm.ospiteId,
      tipo: spesaForm.tipo,
      importo: importoNum,
      descrizione: descrizioneRaw || null,
      data: spesaForm.data,
      note: noteRaw || null,
      escludiDaFatturazione: !!spesaForm.escludiDaFatturazione,
    };
    setSaving(true);
    try {
      if (spesaEditId) {
        await axios.put(
          `${API_URL}/api/oss/me/prima-nota/spese/${spesaEditId}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Spesa aggiornata");
      } else {
        await axios.post(
          `${API_URL}/api/oss/me/prima-nota/spese`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Spesa registrata");
      }
      setShowSpesaDialog(false);
      setSpesaEditId(null);
      spesaFieldDefaultsRef.current = { importo: "", descrizione: "", note: "" };
      setSpesaForm({ ...EMPTY_SPESA, data: todayISO() });
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSpesa = async (s) => {
    if (!isOssPrimaNotaRow(s) || !s.id) return;
    if (!window.confirm("Eliminare questa spesa?")) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/oss/me/prima-nota/spese/${s.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Spesa eliminata");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setSaving(false);
    }
  };

  const tipoAusilioLabel = (v) => TIPI_AUSILIO.find((t) => t.value === v)?.label || v || "—";
  const tipoSpesaLabel = (v) => TIPI_SPESA.find((t) => t.value === v)?.label || v || "—";

  const handlePrint = () => {
    printOssPage("Prima nota");
  };

  const printStampaIl = new Date().toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div>
      <OssPrintDocumentHeader
        kind="Prima nota ospiti"
        title={primaNotaTab === "consumi" ? "Consumi ausiliari" : "Spese extra"}
        lines={[
          `Mese di riferimento: ${formatMese(mese)}`,
          `Stampa: ${printStampaIl}`,
        ]}
      />

      <Tabs value={primaNotaTab} onValueChange={setPrimaNotaTab}>
        <div className="print:hidden">
          <OssSectionPageHeader
            sectionKey="primaNota"
            title="Prima Nota Ospiti"
            subtitle="Registra consumi ausiliari e spese extra per gli ospiti della struttura"
            toolbar={
              <div className="space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 bg-white/80"
                      onClick={() => setMese((m) => navigateMese(m, -1))}
                      aria-label="Mese precedente"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center">
                      {formatMese(mese)}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 bg-white/80"
                      onClick={() => setMese((m) => navigateMese(m, 1))}
                      aria-label="Mese successivo"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-rose-200 text-rose-800 hover:bg-rose-50 bg-white/80"
                    onClick={handlePrint}
                    title="Stampa la scheda attualmente selezionata (Consumi o Spese extra)"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Stampa
                  </Button>
                </div>
                <TabsList className="w-full sm:w-auto h-auto flex flex-wrap justify-start bg-slate-100/80 p-1">
                  <TabsTrigger value="consumi" className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Consumi Extra
                    {consumi.length > 0 && <Badge variant="secondary" className="ml-1">{consumi.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="spese" className="flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Spese extra
                    {spese.length > 0 && <Badge variant="secondary" className="ml-1">{spese.length}</Badge>}
                  </TabsTrigger>
                </TabsList>
              </div>
            }
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16 print:hidden">
            <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
          </div>
        ) : (
          <>
          <TabsContent value="consumi">
            <Card className="card-base print:shadow-none print:border print:border-slate-300 print:break-inside-avoid">
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 print:hidden">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="w-4 h-4 text-rose-600 print:text-rose-700" />
                  Consumi Extra — {formatMese(mese)}
                </CardTitle>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 print:hidden" onClick={openAddConsumo}>
                  <Plus className="w-4 h-4 mr-1" />
                  Nuovo consumo
                </Button>
              </CardHeader>
              <CardContent>
                {consumi.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Nessun consumo registrato per questo mese</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Ospite</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Quantità</TableHead>
                          <TableHead className="text-right">Costo</TableHead>
                          <TableHead className="hidden lg:table-cell">Fatturazione</TableHead>
                          <TableHead className="w-[100px] text-right print:hidden">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {consumi.map((c, idx) => (
                          <TableRow key={c.id || idx}>
                            <TableCell>{formatDate(c.data)}</TableCell>
                            <TableCell>{ospiteMap[c.ospiteId]?.nome_completo || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{tipoAusilioLabel(c.tipoAusilio)}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{c.quantita ?? "—"}</TableCell>
                            <TableCell className="text-right font-medium">
                              {c.costoEuro != null ? `€ ${Number(c.costoEuro).toFixed(2)}` : "—"}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {c.escludiDaFatturazione ? (
                                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                  Esclusa
                                </Badge>
                              ) : (
                                <span className="text-xs text-slate-400">Inclusa</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right print:hidden">
                              {isOssPrimaNotaRow(c) ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-slate-600"
                                    disabled={saving}
                                    onClick={() => openEditConsumo(c)}
                                    aria-label="Modifica consumo"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700"
                                    disabled={saving}
                                    onClick={() => handleDeleteConsumo(c)}
                                    aria-label="Elimina consumo"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="spese">
            <Card className="card-base print:shadow-none print:border print:border-slate-300 print:break-inside-avoid">
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 print:hidden">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Receipt className="w-4 h-4 text-rose-600 print:text-rose-700" />
                  Spese extra — {formatMese(mese)}
                </CardTitle>
                <div className="flex flex-wrap gap-2 print:hidden">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => {
                      openAddSpesa();
                      setShowSpesaAiDialog(true);
                    }}
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    Da scontrino
                  </Button>
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={openAddSpesa}>
                    <Plus className="w-4 h-4 mr-1" />
                    Nuova spesa
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {spese.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Nessuna spesa registrata per questo mese</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Ospite</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead className="text-right">Importo</TableHead>
                          <TableHead className="hidden lg:table-cell">Fatturazione</TableHead>
                          <TableHead className="w-[100px] text-right print:hidden">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {spese.map((s, idx) => (
                          <TableRow key={s.id || idx}>
                            <TableCell>{formatDate(s.data)}</TableCell>
                            <TableCell>{ospiteMap[s.ospiteId]?.nome_completo || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{tipoSpesaLabel(s.tipo)}</Badge>
                            </TableCell>
                            <TableCell>{s.descrizione || "—"}</TableCell>
                            <TableCell className="text-right font-medium">
                              {s.importo != null ? `€ ${Number(s.importo).toFixed(2)}` : "—"}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {s.escludiDaFatturazione ? (
                                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                  Esclusa
                                </Badge>
                              ) : (
                                <span className="text-xs text-slate-400">Inclusa</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right print:hidden">
                              {isOssPrimaNotaRow(s) ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-slate-600"
                                    disabled={saving}
                                    onClick={() => openEditSpesa(s)}
                                    aria-label="Modifica spesa"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700"
                                    disabled={saving}
                                    onClick={() => handleDeleteSpesa(s)}
                                    aria-label="Elimina spesa"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          </>
        )}
      </Tabs>

      <Dialog
        open={showConsumoDialog}
        onOpenChange={(open) => {
          setShowConsumoDialog(open);
          if (!open) {
            setConsumoEditId(null);
            setConsumoForm({ ...EMPTY_CONSUMO, data: todayISO() });
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{consumoEditId ? "Modifica consumo" : "Nuovo consumo ausiliario"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Ospite</Label>
              <Select value={consumoForm.ospiteId} onValueChange={(v) => setConsumoForm((p) => ({ ...p, ospiteId: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona ospite" />
                </SelectTrigger>
                <SelectContent>
                  {fascicoli.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nome_completo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo ausilio</Label>
              <Select value={consumoForm.tipoAusilio} onValueChange={(v) => setConsumoForm((p) => ({ ...p, tipoAusilio: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_AUSILIO.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantità (pezzi singoli)</Label>
              <Input
                key={`consumo-q-${consumoDialogKey}`}
                ref={consumoQuantitaRef}
                type="number"
                min={1}
                className="mt-1"
                defaultValue={consumoFieldDefaultsRef.current.quantita}
              />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" className="mt-1" value={consumoForm.data} onChange={(e) => setConsumoForm((p) => ({ ...p, data: e.target.value }))} />
            </div>
            <BillingExclusionToggle
              id="oss-consumo-escludi-fatturazione"
              checked={!!consumoForm.escludiDaFatturazione}
              onCheckedChange={(value) => setConsumoForm((p) => ({ ...p, escludiDaFatturazione: value }))}
            />
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea
                key={`consumo-n-${consumoDialogKey}`}
                ref={consumoNoteRef}
                className="mt-1"
                rows={2}
                defaultValue={consumoFieldDefaultsRef.current.note}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsumoDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveConsumo} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
              {saving ? "Salvataggio..." : consumoEditId ? "Salva modifiche" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showSpesaDialog}
        onOpenChange={(open) => {
          setShowSpesaDialog(open);
          if (!open) {
            setSpesaEditId(null);
            setSpesaForm({ ...EMPTY_SPESA, data: todayISO() });
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{spesaEditId ? "Modifica spesa extra" : "Nuova spesa extra"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!spesaEditId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-rose-200 text-rose-700 hover:bg-rose-50 gap-2"
                onClick={() => setShowSpesaAiDialog(true)}
              >
                <Camera className="w-4 h-4" />
                Fotografa scontrino (IA)
              </Button>
            ) : null}
            <div>
              <Label>Ospite</Label>
              <Select value={spesaForm.ospiteId} onValueChange={(v) => setSpesaForm((p) => ({ ...p, ospiteId: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona ospite" />
                </SelectTrigger>
                <SelectContent>
                  {fascicoli.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nome_completo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo spesa</Label>
              <Select value={spesaForm.tipo} onValueChange={(v) => setSpesaForm((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_SPESA.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Importo (€)</Label>
              <Input
                key={`spesa-i-${spesaDialogKey}`}
                ref={spesaImportoRef}
                type="text"
                inputMode="decimal"
                className="mt-1"
                placeholder="0,00"
                defaultValue={spesaFieldDefaultsRef.current.importo}
              />
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Input
                key={`spesa-d-${spesaDialogKey}`}
                ref={spesaDescrizioneRef}
                className="mt-1"
                defaultValue={spesaFieldDefaultsRef.current.descrizione}
              />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" className="mt-1" value={spesaForm.data} onChange={(e) => setSpesaForm((p) => ({ ...p, data: e.target.value }))} />
            </div>
            <BillingExclusionToggle
              id="oss-spesa-escludi-fatturazione"
              checked={!!spesaForm.escludiDaFatturazione}
              onCheckedChange={(value) => setSpesaForm((p) => ({ ...p, escludiDaFatturazione: value }))}
            />
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea
                key={`spesa-n-${spesaDialogKey}`}
                ref={spesaNoteRef}
                className="mt-1"
                rows={2}
                defaultValue={spesaFieldDefaultsRef.current.note}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSpesaDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveSpesa} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
              {saving ? "Salvataggio..." : spesaEditId ? "Salva modifiche" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EstraiSpesaDaScontrinoDialog
        open={showSpesaAiDialog}
        onOpenChange={setShowSpesaAiDialog}
        token={token}
        apiPath="/api/oss/me/prima-nota/spese/extract-scontrino"
        ospiti={fascicoli}
        onApply={applySpesaFromAi}
      />
    </div>
  );
}
